import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { payrollService } from '../../../services/payrollService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmployeeDashboard = ({ staffMembers, payrollRecords, selectedPeriod, onRefresh }) => {
  const { currentTenant } = useAuth();
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleViewAttendance = async (staffMember) => {
    setSelectedStaff(staffMember);
    setLoadingAttendance(true);
    try {
      const summary = await payrollService?.getStaffAttendanceSummary(
        currentTenant?.id,
        staffMember?.user_id,
        selectedPeriod?.start,
        selectedPeriod?.end
      );
      setAttendanceSummary(summary || []);
    } catch (error) {
      console.error('Error loading attendance:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const getStaffPayrollRecord = (staffId) => {
    return payrollRecords?.find(r => r?.staff_id === staffId);
  };

  const calculateTotalHours = (staffId) => {
    const record = getStaffPayrollRecord(staffId);
    return record ? (parseFloat(record?.regular_hours || 0) + parseFloat(record?.overtime_hours || 0))?.toFixed(2) : '0.00';
  };

  const filteredStaff = staffMembers?.filter(staff => 
    staff?.user_profiles?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    staff?.user_profiles?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h2 className="text-xl font-semibold text-foreground">Employee Payroll Dashboard</h2>
        <div className="relative">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search employees..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground w-full md:w-64"
          />
        </div>
      </div>

      {/* Staff List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredStaff?.map((staff) => {
          const wageRate = staff?.staff_wage_rates?.[0];
          const payrollRecord = getStaffPayrollRecord(staff?.user_id);
          const totalHours = calculateTotalHours(staff?.user_id);

          return (
            <div key={staff?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {staff?.user_profiles?.avatar_url ? (
                    <img
                      src={staff?.user_profiles?.avatar_url}
                      alt={staff?.user_profiles?.full_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="User" size={24} className="text-primary" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-foreground">{staff?.user_profiles?.full_name}</h3>
                    <p className="text-sm text-muted-foreground capitalize">{staff?.role?.replace('_', ' ')}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  wageRate ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {wageRate ? 'Active' : 'No Rate'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Wage Type</p>
                  <p className="font-medium text-foreground capitalize">
                    {wageRate?.wage_type || 'Not Set'}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Rate</p>
                  <p className="font-medium text-foreground">
                    {wageRate?.wage_type === 'hourly' 
                      ? `$${wageRate?.hourly_rate}/hr`
                      : wageRate?.wage_type === 'salary'
                      ? `$${(wageRate?.annual_salary / 52)?.toFixed(2)}/wk`
                      : 'N/A'
                    }
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Total Hours</p>
                  <p className="font-medium text-foreground">{totalHours} hrs</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Gross Pay</p>
                  <p className="font-medium text-foreground">
                    ${payrollRecord?.gross_pay ? parseFloat(payrollRecord?.gross_pay)?.toFixed(2) : '0.00'}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleViewAttendance(staff)}
                  variant="outline"
                  className="flex-1 text-sm"
                >
                  <Icon name="Clock" size={16} className="mr-1" />
                  View Attendance
                </Button>
                {payrollRecord && (
                  <span className={`px-3 py-2 rounded-lg text-xs font-medium ${
                    payrollRecord?.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                    payrollRecord?.payment_status === 'approved'? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {payrollRecord?.payment_status?.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredStaff?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No employees found</p>
        </div>
      )}

      {/* Attendance Modal */}
      {selectedStaff && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                Attendance Summary - {selectedStaff?.user_profiles?.full_name}
              </h3>
              <button
                onClick={() => setSelectedStaff(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            <div className="p-4">
              {loadingAttendance ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                </div>
              ) : attendanceSummary?.length > 0 ? (
                <div className="space-y-3">
                  {attendanceSummary?.map((record) => (
                    <div key={record?.id} className="border border-border rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Check In</p>
                          <p className="font-medium text-foreground">
                            {new Date(record?.check_in_time)?.toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Check Out</p>
                          <p className="font-medium text-foreground">
                            {record?.check_out_time 
                              ? new Date(record?.check_out_time)?.toLocaleString()
                              : 'Still working'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Lunch Break</p>
                          <p className="font-medium text-foreground">
                            {record?.lunch_duration_minutes || 0} min
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Total Hours</p>
                          <p className="font-medium text-foreground">
                            {record?.total_hours || '0.00'} hrs
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No attendance records for this period</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;